import type { DocOptionRule } from "building-types/options/docOptionRule";
import { type DocOptionEffect } from "building-types/options/optionEffect";
import type { Table } from "common/data/table";
import { stringArrayToSentence } from "common/utils/arrays";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import type { OptionRuleReferencesByOption } from "doc/options/optionRule";
import { getRuleEffects } from "doc/options/optionTools";
import { PlanLibraryBadge } from "lib/components/builders/plan-library/PlanLibraryBadge";
import { planLibraryActions } from "lib/components/builders/plan-library/planLibraryReducer";
import { usePlanLibraryDispatch, usePlanLibrarySelector } from "lib/components/builders/plan-library/planLibraryStore";
import { planLibraryOptionSetWithOverrides } from "lib/components/builders/plan-library/planLibraryUtils";
import { getReadableFormula } from "lib/house/readableFormulaUtils";
import getReadableOptionRules from "lib/options/getReadableOptionRules";
import { useMemo, useState } from "react";
import Modal from "ui/components/Modal";
import cx from "ui/cx";
import type { OptionProps } from "ui/forms/Select";
import Select from "ui/forms/Select";
import IconTriangleDownArrow from "ui/icons/IconTriangleDownArrow";
import StatusIconWarning from "ui/icons/status/IconWarning";
import Tooltip from "ui/popovers/Tooltip";
import styles from "./PlanLibraryOptionRuleModal.module.scss";

/**
 * A component that allows users to override existing option rules
 */

export default function PlanLibraryOptionRuleModal() {
  const dispatch = usePlanLibraryDispatch();

  const { editOptionRules, originalOptionRules, originalOptionEffects, inheritedOptionSets } = usePlanLibrarySelector(
    (state) => {
      return {
        editOptionRules: state.editOptionRules,
        inheritedOptionSets: state.inheritedOptionSets,
        originalOptionRules: state.originalOptionRules,
        originalOptionEffects: state.originalOptionEffects,
      };
    },
  );

  if (!editOptionRules) {
    return null;
  }

  const { ruleIds, optionSetId, optionKey } = editOptionRules;

  // ruleIds must be copied to a new array to avoid mutating the original array
  const sortedIds = [...(ruleIds ?? [])].sort((a, b) =>
    originalOptionRules[a].name.localeCompare(originalOptionRules[b].name),
  );

  function getTitle() {
    if (optionSetId && optionKey) {
      return `Edit Option Rules for ${inheritedOptionSets[optionSetId].options.find((o) => o.id === optionKey)?.name}`;
    } else if (optionSetId) {
      return `Edit Option Rules for ${inheritedOptionSets[optionSetId].name}`;
    }
    return "Edit Option Rules";
  }

  return (
    <Modal
      scrollModal
      open
      onClose={() => dispatch(planLibraryActions.toggleOptionRuleModal({ editOptionRules: null }))}
      title={getTitle()}
    >
      <div className={styles.modalBody}>
        {sortedIds.map((ruleId) => {
          return (
            <EditRule
              key={`${ruleId}-edit-rule`}
              originalOptionRule={originalOptionRules[ruleId]}
              originalEffectsForRule={getRuleEffects(originalOptionEffects, ruleId)}
              expanded={ruleIds.length === 1}
              optionSetId={optionSetId}
              optionKey={optionKey}
            />
          );
        })}
      </div>
    </Modal>
  );
}

interface EditRuleProps {
  originalOptionRule: DocOptionRule;
  originalEffectsForRule: Table<DocOptionEffect>;
  expanded?: boolean;
  optionSetId?: string;
  optionKey?: string;
}

function EditRule({ originalOptionRule, originalEffectsForRule, expanded, optionSetId, optionKey }: EditRuleProps) {
  const dispatch = usePlanLibraryDispatch();
  const [isExpanded, setIsExpanded] = useState(expanded);

  const { optionEffectOverrides, optionSets, optionRuleRefsByOption, originalOptionRules } = usePlanLibrarySelector(
    (state) => {
      return {
        optionEffectOverrides: state.optionEffectOverrides,
        optionSets: state.plans.masterPlan.doc.optionSets,
        originalOptionRules: state.originalOptionRules,
        optionRuleRefsByOption: optionSetId ? state.optionRuleRefsByOptionSets[optionSetId] : null,
      };
    },
  );

  function isOverridden() {
    for (const effectId in optionEffectOverrides) {
      if (originalEffectsForRule[effectId]?.parentId === originalOptionRule.id) {
        return true;
      }
    }
    return false;
  }

  // find specific rule reference for option key / rule combination
  // so we can display contextual rule information for a specific option.
  // boolean options make this a little more complicated since we don't have an
  // optionKey to easily look up the reference
  let foundOptionKey = optionKey;
  let ruleRef: OptionRuleReferencesByOption["string"]["string"] | undefined;
  if (optionKey) {
    ruleRef = optionRuleRefsByOption?.[optionKey][originalOptionRule.id];
  } else if (optionSetId) {
    for (const ruleOptionKey in optionRuleRefsByOption) {
      if (optionRuleRefsByOption[ruleOptionKey][originalOptionRule.id]) {
        foundOptionKey = ruleOptionKey;
        ruleRef = optionRuleRefsByOption[ruleOptionKey][originalOptionRule.id];
        break;
      }
    }
  }

  function helperText(): string {
    const readableFormula = getReadableFormula(originalOptionRule.predicate?.formula ?? "", optionSets);
    // show the trigger if the reference is a "RULE" instead of "EFFECT" or if there is no ruleRef
    if (!ruleRef || ruleRef.type === "RULE") {
      return `[Trigger] This rule is enabled when ${readableFormula}`;
    }

    assertIsNotUndefined(optionSetId);

    const readableEffects = getReadableOptionRules({
      optionSet: optionSets[optionSetId],
      optionSets,
      optionId: foundOptionKey ?? "",
      optionRules: originalOptionRules,
      // we only need to include the reference for a single option
      optionRuleReferences: { [foundOptionKey ?? ""]: { [originalOptionRule.id]: ruleRef } },
      formatForPlanLibrary: true,
    });
    const optionName = optionKey ? optionSets[optionSetId].options[optionKey].name : optionSets[optionSetId].name;
    return `[Effect] ${optionName} ${stringArrayToSentence(readableEffects)}`;
  }

  const sortedEffectIds = useMemo(() => {
    return Object.keys(originalEffectsForRule)
      .filter((id) => optionSets[originalEffectsForRule[id].optionSetId])
      .sort((a, b) => {
        const aOptionSet = optionSets[originalEffectsForRule[a].optionSetId];
        const bOptionSet = optionSets[originalEffectsForRule[b].optionSetId];
        return aOptionSet.name.localeCompare(bOptionSet.name);
      });
  }, []);

  return (
    <div className={styles.rule}>
      <div className={styles.ruleHeader} onClick={() => setIsExpanded(!isExpanded)}>
        <div>
          <IconTriangleDownArrow rotate={isExpanded ? 0 : -90} size={12} />
        </div>
        <div>{originalOptionRule.name}</div>
        <PlanLibraryBadge
          isOverridden={isOverridden()}
          onClickBadge={() => {
            dispatch(planLibraryActions.resetOptionEffectOverrides({ optionRuleId: originalOptionRule.id }));
          }}
        />
      </div>
      <div className={styles.helpText}>{helperText()}</div>
      {isExpanded && (
        <div className={styles.ruleBody}>
          <h3 className={styles.sectionLabel}>Option Effects</h3>
          <div className={styles.helpText}>Choose which options are enabled when the above conditions are true</div>
          <div className={styles.optionEffects}>
            {sortedEffectIds.map((effectId) => (
              <EditOptionEffect
                key={`${effectId}-edit-option-effect`}
                highlighted={originalEffectsForRule[effectId].optionSetId === optionSetId}
                optionEffect={originalEffectsForRule[effectId]}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

interface EditOptionEffectProps {
  optionEffect: DocOptionEffect;
  highlighted?: boolean;
}

function EditOptionEffect({ optionEffect, highlighted }: EditOptionEffectProps) {
  const dispatch = usePlanLibraryDispatch();
  const { optionSet, optionEffectOverrides } = usePlanLibrarySelector((state) => {
    return {
      optionSet: planLibraryOptionSetWithOverrides(
        state.inheritedOptionSets[optionEffect.optionSetId],
        state.overrides[optionEffect.optionSetId],
      ),
      optionEffectOverrides: state.optionEffectOverrides[optionEffect.id],
    };
  });

  function getOverrideValues(): [boolean, string | null | undefined] {
    if ((optionEffectOverrides ?? {}).hasOwnProperty("optionId")) {
      return [true, optionEffectOverrides.optionId ?? null];
    }
    return [false, optionEffect.optionId];
  }

  const [isOverridden, currentValue] = getOverrideValues();

  const optionValues: OptionProps[] = [{ label: "None", value: null }];
  const disabledReasons: string[] = [];

  if (optionSet.disabled) {
    disabledReasons.push(optionSet.name);
  }
  for (const option of optionSet.options) {
    const disabled = option.disabled ?? optionSet.disabled;
    if (!optionSet.disabled && disabled) {
      disabledReasons.push(option.name);
    }
    optionValues.push({
      label: option.name,
      value: option.id,
      displayBehavior: disabled === true ? "disabled" : undefined,
    });
  }

  const handleUpdate = (value) => {
    dispatch(
      planLibraryActions.overrideOptionEffectEnable({
        id: optionEffect.id,
        optionId: value,
      }),
    );
  };

  return (
    <div className={styles.optionEffectRow}>
      <div className={styles.optionEffectLabel}>
        <div className={cx({ styles, highlighted })}>{`${optionSet.name}`}</div>
        {disabledReasons.length > 0 && (
          <Tooltip.Root>
            <Tooltip.Trigger>
              <span>
                <StatusIconWarning />
              </span>
            </Tooltip.Trigger>
            <Tooltip.Content>
              {`${stringArrayToSentence(disabledReasons)} ${
                disabledReasons.length === 1 ? "is" : "are"
              } disabled in this plan`}
            </Tooltip.Content>
          </Tooltip.Root>
        )}
        <PlanLibraryBadge
          isOverridden={isOverridden}
          onClickBadge={() => {
            dispatch(planLibraryActions.resetOptionEffectOverride({ optionEffectId: optionEffect.id }));
          }}
        />
      </div>
      <Select
        disabled={optionSet.disabled === true}
        options={optionValues}
        selectSize="medium"
        onChange={handleUpdate}
        value={currentValue}
      />
    </div>
  );
}
